<template>
    <div class="vision-mission-wrapper">
        <template v-for="(item, index) in json.visionMission" :key="`panel${index}`">
            <div
                class="panel"
                :class="`panel-${(index % 3) + 1}`"
                :ref="`panel-${index}`"
                :zIndex="(index + 1)"
            >
                <!-- Background image -->
                <div class="bgouter">
                    <div class="bgimg" :style="item.visionBackground[0].bgStyle"></div>
                </div>

                <!-- Mini graphics -->
                <div
                    v-for="(graphic, gIndex) in item.visionGraphics"
                    :key="`mini${gIndex}`"
                    class="mini-graphic"
                    :class="`mini-graphic-${gIndex + 1}`"
                    :style="graphic.bgStyle"
                ></div>

                <!-- Title: single text string rendered in overlapping layers -->
                <div class="txts">
                    <div class="txts2">
                        <div class="vision-eye">
                            <VisioneyeIcon/>
                        </div>
                        <div class="subtitle">
                            {{ item.title }}
                        </div>
                        <div class="title-wrap">
                            <!-- Invisible text layer for positioning -->
                            <div class="title-invis">{{ item.message }}</div>
                            <!-- Outlined text (always visible) -->
                            <div class="title-outlined">{{ item.message }}</div>
                            <!-- Filled text (revealed via clip-path wipe effect) -->
                            <div class="title-filled">{{ item.message }}</div>
                        </div>
                        <div class="final-link" v-if="index === json.visionMission.length - 1">
                            <Link
                                href="/careers"
                                target="_self"
                                label="Learn More"
                                :isButton="false"
                                :addCaret="true"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="panel-spacer"></div>
        </template>
    </div>
</template>

<script>
import gsap from '@/plugins/gsap';
import ScrollTrigger from '@/plugins/gsap/ScrollTrigger';
import SplitText from '@/plugins/gsap/SplitText';
import Link from "@/components/pieces/Link.vue";
import VisioneyeIcon from "@/components/svgs/Visioneye.vue";
gsap.registerPlugin(ScrollTrigger, SplitText);
export default {
    name: "VisionMission",
    components: {Link, VisioneyeIcon},
    props: {
        json: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            // Factor (pixels per second of animation); adjust as needed.
            finalFactor: 3000,
            // References to the final panel’s timeline and spacer for responsive updates.
            finalTimeline: null,
            finalSpacer: null,
            miniStaggerDelay: 0.05
        };
    },
    mounted() {
        setTimeout(this.initTriggers,50)

        // Listen for window resize to responsively update the final spacer.
        window.addEventListener("resize", this.updateSpacerHeight);


    },
    beforeUnmount() {
        window.removeEventListener("resize", this.updateSpacerHeight);
    },
    methods: {
        initTriggers() {
            //const width = window.visualViewport.width;
            //const height = window.visualViewport.height;
            //const aspectRatio = (width / height).toFixed(2);
            //console.info(`Width: ${width}px`);
            //console.info(`Height: ${height}px`);
            //console.info(`Aspect Ratio: ${aspectRatio}`);
            //alert(width + '-' + height + "  " + aspectRatio)

            this.updateSpacerHeight();

            this.json.visionMission.forEach((item, index) => {
                let panelRef = this.$refs[`panel-${index}`];
                // Since refs now point directly to DOM elements:
                const panelEl = Array.isArray(panelRef) ? panelRef[0] : panelRef;

                if (!panelEl || typeof panelEl.querySelectorAll !== "function") {
                    console.error(`Panel ref for index ${index} is not a valid DOM element.`);
                    return;
                }

                const miniGraphics = panelEl.querySelectorAll(".mini-graphic");
                const titleInvis = panelEl.querySelector(".title-invis");
                const titleOutlined = panelEl.querySelector(".title-outlined");
                const titleFilled = panelEl.querySelector(".title-filled");
                const bgimg = panelEl.querySelector(".bgimg");

                new SplitText(titleOutlined, { type: "lines" });
                const splitFilled = new SplitText(titleFilled, { type: "lines" });
                new SplitText(titleInvis, { type: "lines" });

                gsap.set(splitFilled.lines, { clipPath: "inset(0 100% 0 0)" });
                [titleInvis, titleOutlined, titleFilled].forEach(el => {
                    gsap.set(el, { width: "calc(100% + 30px)", left: "-15px" });
                });
                gsap.set(bgimg, { filter: "grayscale(50%)" });

                // Define base ScrollTrigger config.
                let scrollTriggerConfig = {
                    trigger: panelEl,
                    start: "top top",
                    scrub: true,
                    pin: true,
                    pinSpacing: true,
                    // markers: true, // Uncomment for debugging.
                };

                // Build the timeline for panel animations.
                let tl = gsap.timeline({
                    scrollTrigger: scrollTriggerConfig,
                });

                const miniArray = Array.from(miniGraphics);
                // Randomize the order
                miniArray.sort(() => Math.random() - 0.5);

                tl.fromTo(
                    miniArray,
                    { scale: 0.5, opacity: 0, filter: "grayscale(100%)" },
                    {
                        scale: 1,
                        opacity: 1,
                        filter: "grayscale(0%)",
                        ease: "none",
                        duration: 0.65,
                        stagger: this.miniStaggerDelay,
                    },
                    0
                );

                tl.fromTo(
                    bgimg,
                    { scale: 1.1 },
                    { scale: 1, ease: "none", duration: 0.2 },
                    0
                );


                tl.to(
                    bgimg,
                    { filter: "grayscale(0%)", ease: "none", duration: 1 },
                    0
                );

                tl.to(
                    splitFilled.lines,
                    { duration: 0.15, clipPath: "inset(0 0% 0 0)", ease: "none", stagger: 0.07 },
                    0
                );
                // let newDuration = 1 - (splitFilled.lines?.length * 0.07)
                // console.log('newDuration', newDuration)
                // tl.to(
                //     splitFilled.lines,
                //     { duration: newDuration, clipPath: "inset(0 0% 0 0)", ease: "none", stagger: 0.07 },
                //     0
                // );

                // For the final panel, dynamically calculate spacer height and update unpin logic.
                if (index === this.json.visionMission.length - 1) {
                    const timelineDuration = tl.duration(); // seconds
                    const extraSpacer = 500; // extra scroll space to ensure the animation completes
                    const finalScroll = timelineDuration * this.finalFactor + extraSpacer; // computed scroll distance in px
                    scrollTriggerConfig.end = `+=${finalScroll}`;

                    // Use onLeave to unpin and then reset the spacer's height
                    scrollTriggerConfig.onLeave = self => {
                        // Clear pin styles so the panel unpins cleanly.
                        gsap.set(panelEl, { clearProps: "position,pinSpacing,top" });
                        self.disable();
                        // Remove extra spacer height after animation completes
                        if (this.finalSpacer) {
                            this.finalSpacer.style.height = '0px';
                        }
                    };

                    // Save final panel references for responsive updates.
                    this.finalTimeline = tl;
                    const spacer = panelEl.nextElementSibling;
                    if (spacer && spacer.classList.contains("panel-spacer")) {
                        spacer.style.height = `${finalScroll}px`;
                        this.finalSpacer = spacer;
                    }
                } else {
                    // For non-final panels, use your existing static value.
                    scrollTriggerConfig.end = "+=3500vh";
                }
            });
        },
        updateSpacerHeight() {
            if (this.finalTimeline && this.finalSpacer) {
                const timelineDuration = this.finalTimeline.duration();
                const finalScroll = timelineDuration * this.finalFactor;
                this.finalSpacer.style.height = `${finalScroll}px`;
                // Update the ScrollTrigger's end value and refresh its measurements.
                this.finalTimeline.scrollTrigger.vars.end = `+=${finalScroll}`;
                this.finalTimeline.scrollTrigger.refresh();
            }
            setTimeout(this.positionMiniGraphics, 100)
            setTimeout(this.positionMiniGraphics, 500)
        },
        positionMiniGraphics() {
            this.$nextTick(() => {
                const refs = this.$refs;

                const getHeight = (el, ratio) => el ? el.offsetWidth / ratio : 0;
                const getWidth = (el) => el ? el.offsetWidth : 0;

                for (let i = 0; i < this.json.visionMission.length; i++) {
                    const panel = refs[`panel-${i}`];
                    const panelEl = Array.isArray(panel) ? panel[0] : panel;
                    if (!panelEl) continue;

                    const isPanel1 = panelEl.classList.contains('panel-1');
                    const isPanel2 = panelEl.classList.contains('panel-2');
                    const isPanel3 = panelEl.classList.contains('panel-3');

                    const g = n => panelEl.querySelector(`.mini-graphic-${n}`);

                    if (isPanel1) {
                        const h3 = getHeight(g(3), 1014 / 622);
                        const h1 = getHeight(g(1), 836 / 608);
                        const w4 = getWidth(g(3));
                        if (g(2)) g(2).style.bottom = `${h3}px`;
                        if (g(5)) g(5).style.top = `${h1}px`;
                        if (g(4)) g(4).style.right = `${w4}px`;
                    }

                    if (isPanel2) {
                        const h3 = getHeight(g(3), 836 / 608);
                        const h1 = getHeight(g(1), 708 / 434);
                        if (g(2)) g(2).style.bottom = `${h3}px`;
                        if (g(5)) g(5).style.top = `${h1}px`;
                    }
                    if (isPanel3) {
                        const h1 = getHeight(g(1), 684 / 420);
                        if (g(4)) g(4).style.top = `${h1}px`;
                    }
                }
            });
        }
    },
};
</script>
